'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { useInboxTheme, INBOX_THEMES } from '@/contexts/InboxThemeContext';

interface InboxTopbarProps {
  onToggleSidebar: () => void;
}

export default function InboxTopbar({ onToggleSidebar }: InboxTopbarProps) {
  const [searchValue, setSearchValue] = useState('');
  const [showThemePicker, setShowThemePicker] = useState(false);
  const { user, signOut, isAdmin } = useAuth();
  const router = useRouter();
  const { theme, setTheme, themeConfig } = useInboxTheme();

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/');
      router.refresh();
    } catch {
      toast.error('Failed to sign out');
    }
  };

  const initials = user?.user_metadata?.full_name
    ? user.user_metadata.full_name.charAt(0).toUpperCase()
    : user?.email?.charAt(0).toUpperCase() ?? 'U';

  return (
    <header className="h-14 flex items-center gap-3 px-4 inbox-glass-topbar flex-shrink-0 z-10 relative">
      {/* Sidebar toggle */}
      <button
        onClick={onToggleSidebar}
        className="p-2 rounded-lg inbox-text-muted hover:inbox-text-foreground hover:bg-white/10 transition-all duration-150"
        aria-label="Toggle sidebar"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      <Link href="/" className="flex items-center gap-2 mr-2 flex-shrink-0">
        <div className="inbox-glass rounded-lg p-1">
          <AppLogo size={22} />
        </div>
        <span className="font-bold text-base tracking-tight inbox-text-foreground hidden sm:block">
          CompanyInbox
        </span>
      </Link>

      {/* Search */}
      <div className="flex-1 max-w-md">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 inbox-text-muted" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search messages..."
            className="inbox-input pl-9 pr-4 py-2 text-sm"
          />
        </div>
      </div>

      <div className="ml-auto flex items-center gap-2">
        {/* Theme picker button */}
        <div className="relative">
          <button
            onClick={() => setShowThemePicker(!showThemePicker)}
            className="flex items-center gap-1.5 p-2 rounded-lg hover:bg-white/10 transition-all duration-150 inbox-text-muted hover:inbox-text-foreground"
            title="Change inbox theme"
            aria-label="Theme picker"
          >
            <div
              className="w-4 h-4 rounded-full border-2 border-white/30"
              style={{ background: themeConfig.gradient }}
            />
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/>
            </svg>
          </button>

          {showThemePicker && (
            <div className="absolute right-0 top-full mt-2 w-52 theme-picker-panel p-3 z-50 animate-slide-up">
              <p className="text-xs font-semibold inbox-text-muted uppercase tracking-wider mb-3 px-1">Inbox Theme</p>
              <div className="space-y-1">
                {INBOX_THEMES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { setTheme(t.id); setShowThemePicker(false); toast.success(`Theme: ${t.label}`); }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 ${
                      theme === t.id ? 'bg-white/15 inbox-text-foreground' : 'hover:bg-white/10 inbox-text-secondary'
                    }`}
                  >
                    <div
                      className="w-5 h-5 rounded-full flex-shrink-0 border-2"
                      style={{
                        background: t.gradient,
                        borderColor: theme === t.id ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)',
                      }}
                    />
                    <span className="text-sm font-medium">{t.label}</span>
                    {theme === t.id && (
                      <svg className="ml-auto" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Notifications */}
        <button
          onClick={() => toast.info('3 new messages in your inbox')}
          className="relative p-2 rounded-lg inbox-text-muted hover:inbox-text-foreground hover:bg-white/10 transition-all duration-150"
          aria-label="Notifications"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 01-3.46 0" />
          </svg>
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border border-black/20" />
        </button>

        {/* Admin link */}
        {isAdmin && isAdmin() && (
          <Link
            href="/admin"
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/20 border border-amber-400/30 text-amber-300 text-xs font-semibold hover:bg-amber-500/30 transition-all duration-150"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Admin
          </Link>
        )}

        {/* Upgrade CTA */}
        <Link
          href="/upgrade-pricing-screen"
          className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all duration-150"
          style={{
            background: `${themeConfig.color}22`,
            borderColor: `${themeConfig.color}44`,
            color: themeConfig.color,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
            <polyline points="17 6 23 6 23 12" />
          </svg>
          Upgrade
        </Link>

        {/* Avatar with dropdown */}
        <div className="relative group">
          <button
            className="w-8 h-8 rounded-full border-2 flex items-center justify-center font-semibold text-sm cursor-pointer transition-all duration-150"
            style={{
              background: `${themeConfig.color}25`,
              borderColor: `${themeConfig.color}50`,
              color: themeConfig.color,
            }}
          >
            {initials}
          </button>
          <div className="absolute right-0 top-full mt-2 w-48 inbox-glass-intense rounded-xl shadow-elevated opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
            <div className="p-3 border-b border-white/10">
              <p className="text-xs font-semibold inbox-text-foreground truncate">{user?.email ?? 'Guest'}</p>
            </div>
            <div className="p-1">
              <Link href="/domains" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm inbox-text-secondary hover:inbox-text-foreground hover:bg-white/10 transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                </svg>
                My Domains
              </Link>
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}